import './styles/graphic.scss';
import * as d3 from 'd3';
import 'd3-transition';
import { geoAlbersUsaTerritories } from 'd3-composite-projections';
import { legendColor, legendHelpers } from 'd3-svg-legend';
import { ckmeans } from 'simple-statistics';
import * as topojson from 'topojson';
import { classify } from './utils/helpers';
import * as colors from './utils/colors';
import meta from './data/meta.json';
import data from './data/data.json';
import us from './data/us-states-20m.json';

d3.selection.prototype.moveToFront = function () {
  return this.each(function () {
    this.parentNode.appendChild(this);
  });
};

legendHelpers.thresholdLabels = function ({
  i,
  genLength,
  generatedLabels,
  labelDelimiter,
}) {
  if (i === 0) {
    const values = generatedLabels[i].split(` ${labelDelimiter} `);
    return `1 ${labelDelimiter} ${values[1]}%`;
  }
  if (i === genLength - 1) {
    const values = generatedLabels[i].split(` ${labelDelimiter} `);
    return `${values[0]}% or more`;
  }
  return `${generatedLabels[i]}%`;
};

const positionTooltip = (event) => {
  const containerRect = d3.select('#graphic').node().getBoundingClientRect();
  const overlayRect = d3.select('.tooltip').node().getBoundingClientRect();
  const position = {};
  const local = {
    x: event.clientX - containerRect.left,
    y: event.clientY - containerRect.top,
  };
  const space = {
    right: window.innerWidth - event.clientX - 10,
    bottom: window.innerHeight - event.clientY - 20,
  };
  if (space.right > overlayRect.width + 10) {
    position.left = `${local.x + 10}px`;
    position.right = 'initial';
  } else {
    position.left = 'initial';
    position.right = `${10}px`;
  }
  position.top = (space.bottom > overlayRect.height + 20) ? `${local.y + 10}px` : `${local.y - overlayRect.height - 10}px`;

  return position;
};

const handleOver = (d) => {
  if (d.properties.status) {
    const newStyles = positionTooltip(event);
    const toolTip = d3.select('.tooltip');

    const string = `
    <div class="header">
    <h6 class="title">${d.properties.name}</h6>
    <p class="amt"><span>Incumbent:</span> ${d.properties.race}</p>
    <p class="amt"><span>Vulnerability:</span> ${d.properties.vulnerability}</p>
    </div>`;
    toolTip.html(string);

    toolTip
      .style('top', newStyles.top)
      .style('left', newStyles.left)
      .style('right', newStyles.right)
      .style('visibility', 'visible');
  } else {
    handleOut();
  }
};

const handleOut = () => {
  const toolTip = d3.select('.tooltip');
  toolTip.style('visibility', 'hidden');
};

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    classes,
    hue,
    // width,
    // height
  } = props;

  // console.log(colors.business.green[classes - 1]);

  const mapHeight = initialWidth * (500 / 890);

  const tooltip = container.append('div').attr('class', 'tooltip');

  const legendSVG = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', 0);
    // .attr('height', 75);

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', mapHeight);

  // draw here
  const projection = geoAlbersUsaTerritories()
    .translate([initialWidth / 2, mapHeight / 2])
    .scale(initialWidth * 1.2);

  const path = d3.geoPath()
    .projection(projection);

  const draw = (data) => {
    let counties = topojson.feature(us, us.objects.states).features;
    counties = counties.map((geography) => {
      const datum = data.find(d => d.fips === geography.id);
      if (datum) {
        geography.properties.usps = datum.usps;
        geography.properties.name = datum.name;
        geography.properties.value = datum.value * 100;
        geography.properties.status = datum.status;
        geography.properties.vulnerability = datum.vulnerability;
        geography.properties.race = datum.race;
      }
      return geography;
    });

    // Diagonal line pattern for Georgia - First add the background...
    svg
      .append('defs')
      .append('pattern')
      .attr('id', 'textureGA')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('width', 4)
      .attr('height', 4)
      .append('rect')
      .attr('width', 4)
      .attr('height', 4)
      .attr('transform', 'translate(0,0)')
      .attr('fill', '#FFC1B5');
    // ...Then the diagonal lines on top
    d3.select('#graphic svg pattern#textureGA').append('path')
      .attr('d', 'M-1,1 l2,-2, M0,4 l4,-4 M3,5 l2,-2')
      .attr('patternTransform', 'rotate(45)')
      .attr('stroke', '#FFC61C')
      .attr('stroke-width', 1.25);

    const states = svg.append('g')
      .attr('class', 'state-borders')
      .selectAll('path')
      .data(topojson.feature(us, us.objects.states).features)
      .enter()
      .append('path')
      .attr('class', d => `state ${classify(d.properties.name)}`)
      .attr('fill', 'transparent')
      .attr('stroke', '#fff')
      .attr('stroke-width', (isMobile ? 1 : 2))
      .attr('d', path);

    counties = svg.append('g')
      .attr('class', 'counties')
      .selectAll('path')
      .data(counties)
      .enter()
      .append('path')
      // .attr('class', d => (d.properties.status ? `county fips-${d.id} ${classify(d.properties.name)} status-${d.properties.status}` : `county fips-${d.id} ${classify(d.properties.name)} status-none`))
      .attr('class', (d) => {
        if (d.properties.status && d.properties.name != 'Georgia') {
          return `county fips-${d.id} ${classify(d.properties.name)} status-${d.properties.status}`;
        } if (d.properties.name == 'Georgia' && d.properties.vulnerability != 'Tilts Republican (Loeffler), Tossup (Perdue)') {
          return `county fips-${d.id} ${classify(d.properties.name)} status-${d.properties.status}`;
        } if (d.properties.name == 'Georgia' && d.properties.vulnerability == 'Tilts Republican (Loeffler), Tossup (Perdue)') {
          return `county fips-${d.id} ${classify(d.properties.name)}`;
        }
        return `county fips-${d.id} ${classify(d.properties.name)} status-none`;
      })
      // .attr('fill', d => (d.properties.value ? colorScale(d.properties.value) : '#f1f1f1'))
      .attr('fill', (d) => {
        if (d.properties.vulnerability === 'Tilts Republican (Loeffler), Tossup (Perdue)') {
          return 'url(#textureGA)';
        }
      })
      .attr('d', path)
      .on('mousemove', handleOver)
      .on('mouseover', function () { d3.select(this).moveToFront(); })
      .on('mouseout', handleOut);
  };

  if (meta.jsonURL) {
    const url = meta.jsonURL;
    fetch(url)
      .then(res => res.json().then((data) => {
        draw(data.data);
      }));
  } else {
    draw(data);
  }
}
